import React, { Key, useEffect, useState } from 'react';

import { SelectItem } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { StyledMultiCombobox } from './styles';

interface InputFieldProps {
  isDisabled: boolean;
  onChange: (list: string[]) => void;
  values: string[];
}

const InputField = ({ isDisabled, onChange, values }: InputFieldProps) => {
  const [data, setData] = useState(values.filter(Boolean));
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  const handleAddTag = (prev = data) => {
    const next = [...prev, inputValue.trim()].filter(Boolean);
    setData([...new Set(next)]);
    setInputValue('');
  };

  const onSelectionChange = (keys: 'all' | Set<Key>) => {
    handleAddTag(Array.from(keys).map(String));
  };

  const onInputChange = (value: string) => {
    if ([','].includes(value.slice(-1))) {
      handleAddTag();
    } else {
      setInputValue(value);
    }
  };

  const onKeyDown = ({ code }: React.KeyboardEvent) => {
    if ('Enter' === code) {
      handleAddTag();
    }
    if ('Backspace' === code && !inputValue && data.length) {
      setData((prev) => prev.slice(0, -1));
    }
  };

  useEffect(() => {
    if (values.filter(Boolean).join() !== data.join()) {
      onChange(data.length ? data : ['']);
    }
  }, [data, onChange, values]);

  return (
    <StyledMultiCombobox
      data-testid="input-field"
      aria-label={t('common.inputFieldLabel')}
      inputValue={inputValue}
      isDisabled={isDisabled}
      maxHeight={1}
      onInputChange={onInputChange}
      onKeyDown={onKeyDown}
      onSelectionChange={onSelectionChange}
      placeholder={data.length ? '' : t('common.inputFieldPlaceholder')}
      selectedKeys={data}
    >
      {data.map((key) => (
        <SelectItem key={key}>{key}</SelectItem>
      ))}
    </StyledMultiCombobox>
  );
};

export default InputField;
